import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "./ui/button";
import { logError } from "../utils/errorLogger";
import { withTranslation, WithTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";

interface ErrorBoundaryProps extends WithTranslation {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  eventId: string | null;
}

class ErrorBoundaryComponent extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to our error service
    logError(error, errorInfo);

    // Capture the Sentry event ID for the feedback dialog
    Sentry.withScope((scope) => {
      // Convert ErrorInfo to a format Sentry can accept
      scope.setExtras({
        componentStack: errorInfo.componentStack || "Unknown",
      });
      const eventId = Sentry.captureException(error);
      this.setState({ eventId });
    });
  }

  resetErrorBoundary = (): void => {
    this.setState({ hasError: false, error: null, eventId: null });
  };

  // Provide a way for users to send feedback via Sentry
  handleReportFeedback = (): void => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render(): ReactNode {
    const { t } = this.props;

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-2">
            {t("errorBoundary.title")}
          </h2>
          <p className="text-gray-600 mb-4">{t("errorBoundary.description")}</p>
          <p className="text-sm text-gray-500 mb-6">
            {this.state.error?.message}
          </p>

          <p className="text-sm text-gray-500 mb-6">
            {t("errorBoundary.contactSupport")}
          </p>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={this.resetErrorBoundary} variant="outline">
              {t("errorBoundary.tryAgainButton")}
            </Button>
            
            <Button onClick={this.handleReportFeedback} variant="secondary">
              {t("errorBoundary.reportFeedbackButton", "Report Feedback")}
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrap the component with the withTranslation HOC to provide the t function
const ErrorBoundary = withTranslation()(ErrorBoundaryComponent);

export { ErrorBoundary };
